import React from 'react';

import { notify } from 'components-stateless/Notifications';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_DISNEY_GEO_FOOTER } from 'utils/flags/constants';

import {
  FooterWrapper,
  FooterLink,
  Separator,
  LoadingText,
  FooterGlobalStyles,
} from './styles';

const PRIVACY_POLICY_URL = 'https://sites.disney.com/ngr-privacy-policy/';
const CALIFORNIA_NOTICE_URL =
  'https://sites.disney.com/ngr-privacy-policy/#cali-notice-at-collection';

export interface DisneyGeoFooterProps {
  className?: string;
}

export const DisneyGeoFooter: React.FC<DisneyGeoFooterProps> = ({
  className,
}) => {
  const isFeatureEnabled = useFlag(FEATURE_DISNEY_GEO_FOOTER);
  const { geoLocation, isLoading, isError, error } = useGeoLocation({
    enabled: isFeatureEnabled,
  });

  // Return null if feature flag is disabled
  if (!isFeatureEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <>
        <FooterGlobalStyles />
        <FooterWrapper className={className}>
          <div className="footer-content">
            <LoadingText>Loading privacy links...</LoadingText>
          </div>
        </FooterWrapper>
      </>
    );
  }

  // Show toast notification for errors but continue with graceful degradation
  if (isError && error) {
    let errorMessage =
      'Unable to detect your location. Privacy links may not be fully personalized.';

    if (error.isServiceUnavailable) {
      errorMessage =
        'Location service temporarily unavailable. Privacy links may not be fully personalized.';
    } else if (error.status === 400) {
      errorMessage =
        'Location detection failed. Privacy links may not be fully personalized.';
    }

    notify({
      type: 'Warning',
      message: errorMessage,
    });
  }

  const isUSVisitor = geoLocation?.countryCode === 'US';

  return (
    <>
      <FooterGlobalStyles />
      <FooterWrapper className={className}>
        <div className="footer-content">
          <FooterLink
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Disney Privacy Policy (opens in new tab)"
          >
            Privacy Policy
          </FooterLink>

          {isUSVisitor && (
            <>
              <Separator>|</Separator>
              <FooterLink
                href={CALIFORNIA_NOTICE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="California Notice at Collection (opens in new tab)"
              >
                California Notice at Collection
              </FooterLink>
            </>
          )}
        </div>
      </FooterWrapper>
    </>
  );
};
